import {
  t,
  RACE_INFO,
  LeaderboardType,
  getLocale,
  type LeaderboardEntry,
  type RaceId,
} from '@viral-space/shared';
import { attachMobileCloseButton } from './ui-shared';

const COLORS = {
  primary: '#00ff41',
  secondary: '#ffb000',
  danger: '#ff4444',
  dim: 'rgba(0,255,65,0.3)',
  dimText: 'rgba(0,255,65,0.55)',
  bg: 'rgba(0,8,0,0.94)',
  panelBg: 'rgba(0,12,0,0.88)',
  tabActive: '#00ff41',
  selfRow: 'rgba(255,176,0,0.12)',
} as const;

const FONT = "'Share Tech Mono', monospace";
const GLOW = `0 0 8px ${COLORS.primary}, 0 0 16px rgba(0,255,65,0.3)`;

const TABS: Array<{ board: LeaderboardType; labelKey: string; unit: string | null }> = [
  { board: LeaderboardType.XP, labelKey: 'leaderboard.tab.xp', unit: null },
  { board: LeaderboardType.Kills, labelKey: 'leaderboard.tab.kills', unit: null },
  { board: LeaderboardType.SectorsDiscovered, labelKey: 'leaderboard.tab.sectors', unit: null },
];

export interface LeaderboardOptions {
  onRequest: (board: LeaderboardType) => void;
  onClose: () => void;
}

/**
 * Full-screen ladder panel. Shows three boards (XP/level, kills, sectors
 * discovered) as tabbed tables. Data is populated asynchronously from the
 * server: opening the panel triggers a fresh request for the active tab,
 * and {@link LeaderboardUI.setData} is called by `main.ts` when the
 * server's reply arrives.
 */
export class LeaderboardUI {
  private container: HTMLElement;
  private root!: HTMLElement;
  private visible = false;

  private tabsBar!: HTMLElement;
  private tabButtons = new Map<LeaderboardType, HTMLButtonElement>();
  private listEl!: HTMLElement;
  private statusEl!: HTMLElement;
  private selfRankEl!: HTMLElement;

  private activeBoard: LeaderboardType = LeaderboardType.XP;
  private cache = new Map<LeaderboardType, {
    entries: LeaderboardEntry[];
    selfRank: number | null;
    generatedAt: number;
  }>();

  private onRequestCb: (board: LeaderboardType) => void;
  private onCloseCb: () => void;

  private keyListener: ((e: KeyboardEvent) => void) | null = null;

  constructor(container: HTMLElement, opts: LeaderboardOptions) {
    this.container = container;
    this.onRequestCb = opts.onRequest;
    this.onCloseCb = opts.onClose;
    this.build();
  }

  private el(tag: string, styles: Partial<CSSStyleDeclaration>, text?: string): HTMLElement {
    const e = document.createElement(tag);
    Object.assign(e.style, styles);
    if (text !== undefined) e.textContent = text;
    return e;
  }

  private build(): void {
    this.root = this.el('div', {
      position: 'absolute',
      inset: '0',
      display: 'none',
      flexDirection: 'column',
      fontFamily: FONT,
      color: COLORS.primary,
      background: COLORS.bg,
      zIndex: '85',
      pointerEvents: 'auto',
      overflow: 'hidden',
      textTransform: 'uppercase',
      letterSpacing: '1px',
    });
    this.container.appendChild(this.root);

    const header = this.el('div', {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      padding: '16px 24px',
      borderBottom: `1px solid ${COLORS.dim}`,
      flexShrink: '0',
    });

    const titleBlock = this.el('div', { display: 'flex', flexDirection: 'column', gap: '2px' });
    titleBlock.appendChild(this.el('div', {
      fontSize: 'clamp(14px, 2vw, 22px)',
      letterSpacing: '4px',
      textShadow: GLOW,
    }, t('leaderboard.title')));
    titleBlock.appendChild(this.el('div', {
      fontSize: '10px',
      color: COLORS.dimText,
    }, t('leaderboard.subtitle')));
    header.appendChild(titleBlock);

    const closeBtn = document.createElement('button');
    Object.assign(closeBtn.style, {
      fontFamily: FONT,
      fontSize: 'clamp(11px, 1.3vw, 14px)',
      padding: '6px 18px',
      background: 'transparent',
      border: `2px solid ${COLORS.danger}`,
      color: COLORS.danger,
      cursor: 'pointer',
      textTransform: 'uppercase',
      letterSpacing: '3px',
      textShadow: `0 0 6px ${COLORS.danger}`,
      transition: 'all 0.2s',
    });
    closeBtn.textContent = t('leaderboard.close');
    closeBtn.addEventListener('mouseenter', () => {
      closeBtn.style.background = COLORS.danger;
      closeBtn.style.color = '#000';
    });
    closeBtn.addEventListener('mouseleave', () => {
      closeBtn.style.background = 'transparent';
      closeBtn.style.color = COLORS.danger;
    });
    closeBtn.addEventListener('click', () => this.closeAndNotify());
    header.appendChild(closeBtn);
    this.root.appendChild(header);

    attachMobileCloseButton(
      this.root,
      () => this.closeAndNotify(),
      { hideOnMobile: [closeBtn] },
    );

    this.tabsBar = this.el('div', {
      display: 'flex',
      gap: '0',
      padding: '0 24px',
      borderBottom: `1px solid ${COLORS.dim}`,
      flexShrink: '0',
    });
    this.root.appendChild(this.tabsBar);

    for (const tab of TABS) {
      const btn = document.createElement('button');
      Object.assign(btn.style, {
        fontFamily: FONT,
        fontSize: '13px',
        padding: '12px 22px',
        background: 'transparent',
        border: 'none',
        borderBottom: '2px solid transparent',
        color: COLORS.dimText,
        cursor: 'pointer',
        textTransform: 'uppercase',
        letterSpacing: '3px',
        transition: 'all 0.2s',
        marginRight: '4px',
      });
      btn.textContent = t(tab.labelKey);
      btn.addEventListener('click', () => this.setActive(tab.board));
      this.tabsBar.appendChild(btn);
      this.tabButtons.set(tab.board, btn);
    }

    const body = this.el('div', {
      flex: '1',
      minHeight: '0',
      padding: '20px 24px',
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      overflow: 'hidden',
    });
    this.root.appendChild(body);

    this.statusEl = this.el('div', {
      fontSize: '11px',
      color: COLORS.dimText,
      letterSpacing: '2px',
      minHeight: '14px',
    }, t('leaderboard.loading'));
    body.appendChild(this.statusEl);

    const listWrap = this.el('div', {
      flex: '1',
      minHeight: '0',
      overflow: 'auto',
      border: `1px solid ${COLORS.dim}`,
      background: COLORS.panelBg,
    });
    body.appendChild(listWrap);

    this.listEl = this.el('div', {
      display: 'flex',
      flexDirection: 'column',
    });
    listWrap.appendChild(this.listEl);

    this.selfRankEl = this.el('div', {
      fontSize: '11px',
      color: COLORS.secondary,
      letterSpacing: '2px',
      textAlign: 'right',
      minHeight: '14px',
      textShadow: `0 0 6px ${COLORS.secondary}`,
    }, '');
    body.appendChild(this.selfRankEl);

    const footer = this.el('div', {
      padding: '8px 24px',
      borderTop: `1px solid ${COLORS.dim}`,
      fontSize: '9px',
      color: COLORS.dimText,
      letterSpacing: '2px',
      textAlign: 'center',
      flexShrink: '0',
    }, t('leaderboard.hint'));
    this.root.appendChild(footer);
  }

  private setActive(board: LeaderboardType): void {
    this.activeBoard = board;
    for (const [b, btn] of this.tabButtons) {
      const active = b === board;
      btn.style.color = active ? COLORS.tabActive : COLORS.dimText;
      btn.style.borderBottomColor = active ? COLORS.tabActive : 'transparent';
      btn.style.textShadow = active ? GLOW : 'none';
    }
    this.render();
    this.onRequestCb(board);
  }

  private valueLabel(board: LeaderboardType, value: number): string {
    if (board === LeaderboardType.XP) {
      const level = Math.max(1, Math.floor(value / 1_000_000));
      return `${t('leaderboard.level')} ${level}`;
    }
    if (board === LeaderboardType.Kills) {
      return `${value.toLocaleString()} ${t('leaderboard.kills')}`;
    }
    if (board === LeaderboardType.SectorsDiscovered) {
      return `${value.toLocaleString()} ${t('leaderboard.sectors')}`;
    }
    return value.toLocaleString();
  }

  private raceLabel(race: RaceId): string {
    const info = RACE_INFO[race];
    if (!info) return '—';
    return getLocale() === 'es' ? info.nameEs : info.name;
  }

  private render(): void {
    this.listEl.innerHTML = '';
    const cached = this.cache.get(this.activeBoard);

    if (!cached) {
      this.statusEl.textContent = t('leaderboard.loading');
      this.selfRankEl.textContent = '';
      return;
    }

    const { entries, selfRank, generatedAt } = cached;
    const ageMs = Date.now() - generatedAt;
    const ageSec = Math.max(0, Math.round(ageMs / 1000));
    this.statusEl.textContent = t('leaderboard.updated', { seconds: String(ageSec) });

    if (entries.length === 0) {
      const empty = this.el('div', {
        padding: '32px',
        textAlign: 'center',
        color: COLORS.dimText,
        fontSize: '12px',
        fontStyle: 'italic',
      }, t('leaderboard.empty'));
      this.listEl.appendChild(empty);
    } else {
      const header = this.el('div', {
        display: 'grid',
        gridTemplateColumns: '56px 1fr 110px 140px',
        gap: '12px',
        padding: '10px 16px',
        borderBottom: `1px solid ${COLORS.dim}`,
        fontSize: '10px',
        color: COLORS.dimText,
        letterSpacing: '3px',
        background: 'rgba(0,20,0,0.6)',
        position: 'sticky',
        top: '0',
      });
      header.appendChild(this.el('div', {}, '#'));
      header.appendChild(this.el('div', {}, t('leaderboard.col.pilot')));
      header.appendChild(this.el('div', {}, t('leaderboard.col.race')));
      header.appendChild(this.el('div', { textAlign: 'right' }, t('leaderboard.col.score')));
      this.listEl.appendChild(header);

      for (const entry of entries) {
        const row = this.el('div', {
          display: 'grid',
          gridTemplateColumns: '56px 1fr 110px 140px',
          gap: '12px',
          padding: '9px 16px',
          borderBottom: `1px solid rgba(0,255,65,0.08)`,
          fontSize: '12px',
          alignItems: 'center',
        });

        if (selfRank !== null && entry.rank === selfRank) {
          row.style.background = COLORS.selfRow;
          row.style.borderLeft = `3px solid ${COLORS.secondary}`;
          row.style.paddingLeft = '13px';
        }

        const rankColor = entry.rank === 1 ? COLORS.secondary
          : entry.rank === 2 ? '#e0e0e0'
          : entry.rank === 3 ? '#cc7733'
          : COLORS.dimText;

        const rankCell = this.el('div', {
          color: rankColor,
          fontWeight: entry.rank <= 3 ? 'bold' : 'normal',
          letterSpacing: '1px',
          textShadow: entry.rank <= 3 ? `0 0 4px ${rankColor}` : 'none',
        }, `#${entry.rank}`);
        row.appendChild(rankCell);

        const nameCell = this.el('div', {
          color: COLORS.primary,
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          whiteSpace: 'nowrap',
        });
        if (entry.premium) {
          // Gold ★ before the callsign for Premium subscribers. Inline
          // span keeps the existing ellipsis-overflow logic on the
          // wrapper working — only the visible text length changes.
          const star = this.el('span', {
            color: '#ffd700',
            textShadow: '0 0 4px rgba(255,215,0,0.6)',
            marginRight: '4px',
          }, '★');
          nameCell.appendChild(star);
        }
        const nameText = document.createElement('span');
        nameText.textContent = entry.playerName;
        nameCell.appendChild(nameText);
        row.appendChild(nameCell);

        row.appendChild(this.el('div', {
          color: COLORS.dimText,
          fontSize: '11px',
        }, this.raceLabel(entry.race)));

        row.appendChild(this.el('div', {
          textAlign: 'right',
          color: COLORS.secondary,
          fontVariantNumeric: 'tabular-nums',
        }, this.valueLabel(this.activeBoard, entry.value)));

        this.listEl.appendChild(row);
      }
    }

    if (selfRank !== null) {
      this.selfRankEl.textContent =
        `${t('leaderboard.yourRank')}: #${selfRank}`;
    } else {
      this.selfRankEl.textContent = t('leaderboard.yourRankNone');
    }
  }

  /** Called by `main.ts` whenever a `LeaderboardData` message arrives. */
  setData(
    board: LeaderboardType,
    entries: LeaderboardEntry[],
    selfRank: number | null,
    generatedAt: number,
  ): void {
    this.cache.set(board, { entries, selfRank, generatedAt });
    if (this.visible && this.activeBoard === board) {
      this.render();
    }
  }

  private closeAndNotify(): void {
    this.hide();
    this.onCloseCb();
  }

  show(): void {
    if (this.visible) return;
    this.visible = true;
    this.root.style.display = 'flex';
    if (!this.keyListener) {
      this.keyListener = (e: KeyboardEvent) => {
        if (!this.visible) return;
        if (e.code === 'Escape' || e.code === 'KeyL') {
          e.preventDefault();
          e.stopPropagation();
          this.closeAndNotify();
        }
      };
      window.addEventListener('keydown', this.keyListener, true);
    }
    this.setActive(this.activeBoard);
  }

  hide(): void {
    if (!this.visible) return;
    this.visible = false;
    this.root.style.display = 'none';
    if (this.keyListener) {
      window.removeEventListener('keydown', this.keyListener, true);
      this.keyListener = null;
    }
  }

  isVisible(): boolean {
    return this.visible;
  }

  dispose(): void {
    this.hide();
    this.root.remove();
  }
}
